import { useContext, useState } from 'react';
import _ from 'lodash';
import submenuModels from './menus/submenu-models';
import CRUDPage from './CRUDPage';
import NiceDate from '../components/common/NiceDate';
import { ImagePreview } from '../components/images/ImagePreview';
import ScaledMiniature, { useMiniaturesScale } from '../components/miniatures/ScaledMiniature';
import ModelContextEditor from '../components/lego/ModelContextEditor';
import GenDocEditor from '../components/lego/GenDocEditor';
import TrimSpec from '../components/lego/trims/TrimSpec';
import { GEN_COLORS, RESTYLE_COLORS } from '../components/lego/trims/TrimColors';
import LegoAdminPageContext from './legoAdminPageContext';
import LegoContextSummary from '../components/lego/LegoContextSummary';
import ModelIdSelector from '../components/lego/ModelIdSelector';
import MultilineTextEditor from '../components/common/editors/MultilineTextEditor';
import StableUrlsWidget from '../components/lego/stable-urls/StableUrlsWidget';
import { Icon } from '../components/common/Icon';
import { IconButton } from '../components/common/IconButton';
import CountryFlag from '../components/common/CountryFlag';
import { LegoFiltersBar } from '../components/common/LegoFiltersBar';
import { LabelsSummary } from '../components/data-issues/LabelsSummary';
import ModalBatchEditDoc, { defaultColumns } from '../components/lego/ModalBatchEditDoc';
import AIModelSelector from './cerokm/components/AIModelSelector';

function SelectModelIdModal({ defaultModelId, defaultExtraLlmInstruction = '', defaultDoFacelifts = false, defaultAiModel = null, onOk, onCancel }) {
  const [modelId, setModelId] = useState(defaultModelId || null);
  const [extraLlmInstruction, setExtraLlmInstruction] = useState(defaultExtraLlmInstruction || '');
  const [doFacelifts, setDoFacelifts] = useState(!!defaultDoFacelifts);
  const [aiModel, setAiModel] = useState(defaultAiModel || null);

  return <div style={{ minWidth: '520px' }}>
    <div className={'mb-2'}>
      Select a model to generate generations for.
    </div>

    <ModelIdSelector value={modelId} onChange={setModelId} />

    <div className={'mt-3'}>
      <label className={'form-label'}>AI model (optional)</label>
      <AIModelSelector value={aiModel} onChange={setAiModel} />
    </div>

    <div className={'mt-3'}>
      <label className={'form-label'}>Extra LLM instruction (optional)</label>
      <MultilineTextEditor
        value={extraLlmInstruction}
        onChange={setExtraLlmInstruction}
        minRows={2}
        maxRows={6}
      />
    </div>

    <div className={'mt-3 form-check form-switch'}>
      <input
        className={'form-check-input'}
        type={'checkbox'}
        id={'doFaceliftsSwitch'}
        checked={doFacelifts}
        onChange={(e) => setDoFacelifts(e.target.checked)}
      />
      <label className={'form-check-label'} htmlFor={'doFaceliftsSwitch'}>
        Include facelifts/restylings as sub-generations (G1.0, G1.1, ...)
      </label>
    </div>

    <div className={'mt-3'}>
      <button className={'btn btn-primary mr-2'} disabled={!modelId} onClick={() => onOk({ modelId, extraLlmInstruction, doFacelifts, aiModel })}>
        Generate
      </button>
      <button className={'btn btn-secondary'} onClick={onCancel}>
        Cancel
      </button>
    </div>
  </div>;
}

function SelectRemapGenModal({ sourceGen, onOk, onCancel }) {
  const [genId, setGenId] = useState(sourceGen?.remappedToGenId || null);

  const isInvalid = !genId || genId === sourceGen?.genId;

  return <div style={{ minWidth: '520px', minHeight: '350px' }}>
    <div className={'mb-2'}>
      Select an existing generation to remap <TrimSpec fullInfo spec='genId' value={sourceGen?.genId} /> to.
    </div>
    <div className={'mb-3 small text-secondary'}>
      The original generation data (name, years, photo, specs) will be copied into a <code>global</code> country
      entry under the selected generation, and every lego/manual referencing the old generation will be repointed.
    </div>

    <ModelContextEditor
      value={genId ? { genId } : null}
      onChange={(ctx) => setGenId(ctx?.genId || null)}
      groupsToUse={['generations']}
    />

    {genId === sourceGen?.genId ? <div className={'text-danger small mt-2'}>A generation cannot be remapped to itself.</div> : null}

    <div className={'mt-3'}>
      <button className={'btn btn-primary mr-2'} disabled={isInvalid} onClick={() => onOk({ remappedToGenId: genId })}>
        Remap
      </button>
      <button className={'btn btn-secondary'} onClick={onCancel}>
        Cancel
      </button>
    </div>
  </div>;
}

function GenerationsFiltersBar({ filter, onChange }) {
  const { page } = useContext(LegoAdminPageContext)
  const refreshModeloCaches = () => page.runAsync(page.service('/services/data/models').update({}, {}));
  const onFieldChange = (field, value) => {
    let newFilter = _.cloneDeep(filter || {});

    if (field === 'baseModelId') {
      if (value.modelId) {
        value = value.modelId;
      } else if (value.make) {
        value = { $regex: '^' + value.make };
      }
    }

    if (_.isEmpty(value)) {
      delete newFilter[field];
    } else {
      newFilter[field] = _.cloneDeep(value);
    }

    onChange(newFilter);
  };

  let { baseModelId } = filter || {};

  let context = null;
  if(baseModelId?.$regex) {
    context = {make: baseModelId.$regex.slice(1)}
  } else if(baseModelId) {
    context = {modelId: baseModelId}
  }

  return <div className={'d-flex align-items-center'}>
    <span style={{ width: '350px', display: 'inline-block' }} className={'zoom-75 ml-2 mr-2'}>
        <ModelContextEditor value={context}
                            onChange={(newContext) => onFieldChange('baseModelId', newContext || {})}
                            groupsToUse={['makes', 'models', 'families', 'submodels']}/>
      </span>

    <LegoFiltersBar fields={['labels']} onChange={onChange} filter={filter} />

    <span className={'ml-4 mr-2 btn btn-sm btn-warning'} onClick={refreshModeloCaches}>Refresh site generations cache</span>

    <StableUrlsWidget/>
  </div>;
}

// Miniature cell for the gen list: ScaledMiniature self-fetches the doc by id (batched/cached), and
// one shared scale is derived across all the gens' miniatures so they're size-comparable.
function GenMiniatureCell({ miniatureId, allMiniatureIds }) {
  const pxPerMm = useMiniaturesScale(allMiniatureIds, { maxHeight: 80, maxWidth: 150 });
  if (!miniatureId) return null;
  return <div className={'d-inline-block'} style={{ marginTop: '-10px', marginBottom: '-15px' }}>
    <ScaledMiniature miniatureId={miniatureId} pxPerMm={pxPerMm} crop margin={0} />
  </div>;
}

export default class GenerationsManager extends CRUDPage {
  constructor(props) {
    super(props, '/services/data/models/gens');

    this.submenu = submenuModels;
    this.state.columnSort = { index: 1, asc: true, sorter: this.getColumnsDefinition()[1].sorter }
    this.newObjectTemplate = {
      baseModelId: null,
      fromYear: 0,
      toYear: 0,
      gen: '1',
      genId: '',
      specs: null,
    }

    this.remapService = this.service('/services/data/models/gens/remap');
  }

  async openRemapGen(gen) {
    const selection = await new Promise((resolve) => {
      let resolved = false;
      const onResolve = (value) => {
        resolved = true;
        resolve(value);
        this.closeModal();
      };

      this.openModal(
        <SelectRemapGenModal sourceGen={gen} onOk={onResolve} onCancel={() => onResolve(null)} />,
        {
          title: `Remap generation ${gen.genId}`,
          minSize: false,
          onClose: () => !resolved && resolve(null),
        }
      );
    });

    if (!selection?.remappedToGenId) return;

    const { remappedToGenId } = selection;

    const result = await this.runAsync(
      this.remapService.update(null, { genId: gen.genId, remappedToGenId }),
      `Remapping ${gen.genId} → ${remappedToGenId}...`
    );

    await this.fetchObjects();

    this.showNotification({
      level: 'success',
      title: 'Generation remapped',
      body: `${gen.genId} → ${remappedToGenId}. Updated ${result?.legosUpdated || 0} legos and ${result?.manualsUpdated || 0} manuals.`,
      time: new Date(),
    });
  }

  getFacetFiltersComponent() {
    return <>
      <div className={'d-flex align-items-center'}>
        <GenerationsFiltersBar onChange={this.onFiltersChange.bind(this)} filter={this.state.facetFilters} />
        <IconButton icon={'assistant'} fill={true} level={'violeta'} small className='ml-2' onClick={this.openAiGenerations.bind(this)}>AI Generations...</IconButton>
      </div>
    </>
  }  

  // getSearchHandler() {
  //   const dataIssueSearchFilters = {
  //     label: {
  //       filter: labelFilter => (l => (l.labels?.length && (!labelFilter || _.some(l.labels, x => x.includes(labelFilter.toLowerCase()))))),
  //       description: 'With labels [=<prefix>...]'
  //     },
  //     s: {
  //       filter: txt => (l => l.state && (!txt || l.state.startsWith(txt))),
  //       description: 'With state [=<prefix>...]'
  //     },
  //   };
  //
  //   const nodeTextSearchCustomizer = text => text.replace(/\s+/g, '[\\w_\\s]*');
  //
  //   return new ObjectSearchHandler(dataIssueSearchFilters, nodeTextSearchCustomizer);
  // }

  async openBatchEditLabels(docs) {
    const svc = this.service('/services/data/models/gens');
    const columns = [defaultColumns.labels];
    this.openModal(
      <ModalBatchEditDoc docs={docs} onCancel={() => this.closeModal()} onSave={() => true} service={svc} columns={columns}/>,
      { title: 'Edit generations labels in batch' }
    );
  }

  getSelectionButtons(selection) {
    if (selection?.length) {
      return <>
        {super.getSelectionButtons(selection)}
        <IconButton icon={'app_registration'} title={'Edit labels in batch'} onClick={() => this.openBatchEditLabels(selection)}/>
      </>;
    }
    return super.getSelectionButtons(selection);
  }

  getColumnsDefinition(objects) {    
    return [
      { content: 'Last update', className: 'text-center', sorter: 'updatedAt' },
      { content: 'Gen Id', className: 'text-center', sorter: ['baseModelId', g => g.gen.match(/^\d+$/) ? parseInt(g.gen) : 0] },
      { content: 'photo', className: 'text-center'},
      { content: 'Miniature', className: 'text-center'},
      { content: 'Gen', className: 'text-left', sorter: ['baseModelId', 'gen'] },
      { content: 'Name', className: 'text-center' },
      { content: 'from - to', className: 'text-center'},
      { content: 'photos', className: 'text-left'},
    ];
  }

  getRowClasses(obj, i, rows) {
    const { baseModelId, remappedToGenId } = obj;
    let prevRowBaseModelId = rows?.[i-1]?.baseModelId;
    let classes = (prevRowBaseModelId !== baseModelId ? 'row-border-top-3' : '');
    if (remappedToGenId) {
      classes += ' grayscale-row';
    }
    return classes;
  }

  async openAiGenerations() {
    const aiSvc = this.service('/services/ai/build-model-generations');

    let defaultModelId = this.state?.facetFilters?.baseModelId;

    const selection = await new Promise((resolve) => {
      let resolved = false;
      const onResolve = (value) => {
        resolved = true;
        resolve(value);
        this.closeModal();
      };

      this.openModal(
        <SelectModelIdModal defaultModelId={defaultModelId} onOk={onResolve} onCancel={() => onResolve(null)} />,
        {
          title: 'Create AI Generations',
          minSize: false,
          onClose: () => !resolved && resolve(null),
        }
      );
    });

    if (!selection?.modelId) return;
    
    const { modelId, extraLlmInstruction, doFacelifts, aiModel } = selection;
    const gens = await this.runAsync(aiSvc.create({ modelId, extraLlmInstruction, doFacelifts, aiModel }), `Generating generations for ${modelId}...` );

    if (_.isArray(gens) && gens.length) {
      this.importJSON(gens);
      this.showNotification({
        level: 'success',
        title: 'AI generations loaded',
        body: `Loaded ${gens.length} generations for ${modelId}. Review them and click “Save changes”.`,
        time: new Date(),
      });
    } else {
      await this.alert(`No generations returned for ${modelId}.`, 'AI generations');
    }
  }

  getObjectColumns(generation,def,i,allRows) {
    let {
      genId, baseModelId, gen, specs, name, labels, codenames, nicknames, manufacturing, fromYear, toYear, photo, referencePhotos, notes, updatedAt, createdAt, updatedBy, otherModelRelations, otherMarketNames, remappedToGenId
    } = generation;

    let genColors = _.mapValues(GEN_COLORS, (color) => `bg-${color}`);

    let genColorClass = genColors[gen.toString().split('.')[0].replace(/[a-f]$/ig, '')] || 'bg-light-secondary';


    let faceliftClass = '';
    
    const isSubgen = gen.toString().split('.').length > 1;
    if(isSubgen) {
      let restyleN = gen.toString().split('.')[1];
      genColorClass += ' text-black-50'
      faceliftClass = ` align-middle border border-2 border-${RESTYLE_COLORS[restyleN] || 'info'} ${genColorClass} px-2 rounded inline-block`;
    }

    return [
      <td key={'id'} >
        { updatedAt && <NiceDate t={updatedAt}/> }
        {labels?.length ? <><br/><LabelsSummary labels={labels} /></> : null}
      </td>,

      <td  key={'count'} >
        <code className={'text-primary'}>{genId}</code>

        {remappedToGenId ? <div className={'badge badge-secondary ml-1'}>
          Remapped to <TrimSpec spec={'genId'} value={remappedToGenId} />
        </div> : null}

        <div className={'mt-1'}>
          <IconButton icon={'merge'} small level={'violeta'}
                      description={'Remap this generation to an existing one'}
                      onClick={() => this.openRemapGen(generation)}>Remap</IconButton>
        </div>
      </td>,

      <td  key={'blocked'} >
        {photo?.url ?
        <ImagePreview img50 zoom={true} url={photo?.url}/> : null }
      </td>,

      <td key={'miniature'} className={'text-center'}>
        <GenMiniatureCell miniatureId={generation.miniatureId} allMiniatureIds={_.compact(_.map(allRows, 'miniatureId'))}/>
      </td>,

      <td  key={'modelId'} className={genColorClass}>
        <div className={'h5 text-left pl-2 no-wrap'}>
          <TrimSpec spec={'modelId'} className={'mr-3'} value={baseModelId}/>&nbsp;{gen}
        </div>
      </td>,

      <td  key={'name'} className={' '+genColorClass}>
        <div>
          {isSubgen ? <Icon icon={'subdirectory_arrow_right'} level={'secondary'} /> : null}

          <span className={faceliftClass}>
            <span className={isSubgen ? 'h6' : 'h5'}> {name || '-'}</span> 
            <em className={'h6 text-secondary'}>{(codenames || []).join(', ')}</em>
            
            {nicknames?.length ? <em className={'small text-info ml-2'}>"{(nicknames || []).join('", "')}"</em> : null}
          </span>

          <div className={'h5 text-left pl-2'}>
            {specs && <LegoContextSummary context={specs} className={'zoom-75'}/>}
          </div>                
        </div>

        {otherMarketNames && <div className={'text-left zoom-75'}>
          {otherMarketNames.map(name => <span className={'badge badge-violeta mr-1'}>{name}</span>)}
        </div>
        }
        
        {
          otherModelRelations && <div className={'text-left zoom-75'}>
          {_.toPairs(otherModelRelations).map(([relation, relations]) =>
            <div key={relation}>
              {relation}: {relations.map(r => <span className={'badge badge-violeta mr-1'} title={r.why}>{r.with}</span>)}</div>)}
        </div>
        }

        {manufacturing?.length ? <div className={'text-left zoom-75'}>
          🏭{manufacturing.map(m => <span className={'mr-1'}>
            <CountryFlag round countryCode={m.countryCode} title={`${m.countryCode} - ${m.plant} ${m.fromDate ? `(${m.fromDate})` : ''} ${m.toDate ? `- ${m.toDate || ''}` : ''}`}/>            
            </span>)}
        </div> : null}
      </td>,

      <td  key={'fromYear'} className={'text-center text-info'}>
        <span className='text-left d-inline-block' style={{minWidth: '80px'}}>{fromYear} - {toYear}</span>
      </td>,

      <td  key={'refpics'} >
        { (referencePhotos || []).slice(0,4).map(ph => <ImagePreview key={ph.url} img50 zoom={true} url={ph.url}/>) }
      </td>,
    ];
  }

  getObjectEditorComponent(obj, defaultOnSaveCbk, defaultOnCancelCbk) {
    // No miniature bookkeeping needed: on save the gen's `miniatureId` prop changes, so the cell's
    // ScaledMiniature re-fetches the new doc automatically (and the shared scale recomputes).
    return <GenDocEditor obj={obj} onSave={defaultOnSaveCbk} onCancel={defaultOnCancelCbk} />;
  }
}
